import {
  CommandDefinition,
  CommandGroupDefinition,
  CommandResultClose,
  CommandResultEntry,
  CommandResultList,
  CommandResultReturn,
} from '@app/shared/quick-commands/index';
import { Observable, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

export const CommandHelper = {
  /**
   * Close the quick command dialog.
   */
  close(): CommandResultClose {
    return {
      type: 'close',
    };
  },

  commandGroup(options: CommandGroupDefinition): CommandDefinition<unknown> {
    return {
      action: (query, [commandName, ...args]) => {
        if (commandName != null) {
          return options.commands.find((command) => command.name === commandName)!.action(query, args);
        }

        return CommandHelper.list({
          sort: 'asc',
          values: options.commands.map((command): CommandResultEntry => {
            return {
              label: typeof command.label === 'function' ? command.label() : command.label,
              value: command.name,
            };
          }),
        });
      },
      filter: 'automatic',
      ...options,
    };
  },

  /**
   * Return a list of available actions for the user to choose from.
   */
  list<T>(options: Omit<CommandResultList<T>, 'type'>): CommandResultList<T> {
    return {
      ...options,
      type: 'list',
    };
  },

  list$<T, U>(
    value$: Observable<T>,
    cb: (value: T) => Omit<CommandResultList<U>, 'type'>,
    options?: AsyncListOptions
  ): Observable<CommandResultList<U>> {
    const result$ = value$.pipe(
      map((value): CommandResultList<U> => {
        return {
          ...cb(value),
          type: 'list',
        };
      })
    );

    if (options?.debounce != null) {
      return timer(options.debounce).pipe(switchMap(() => result$));
    }

    return result$;
  },

  return(options?: ReturnOptions): CommandResultReturn {
    return {
      refresh: options?.refresh ?? false,
      type: 'return',
    };
  },
};

interface ReturnOptions {
  refresh: boolean;
}

interface AsyncListOptions {
  /**
   * Debounce the result by the specified value (in ms).
   */
  debounce?: number;
}
